import { useEffect, useState } from "react";
import { IMessageEvent, w3cwebsocket as W3CWebSocket } from "websocket";
import { getIsOnpremise } from "../utils";
import getEnv from "../utils/getEnv";

const isOnpremise = getIsOnpremise();

const getWebsocketURL = () => {
  if (typeof window === "undefined") {
    return getEnv("NEXT_PUBLIC_WS_URL");
  }

  if (!isOnpremise) {
    return getEnv("NEXT_PUBLIC_WS_URL");
  }

  const hostname = window?.location.hostname;
  return `ws://${hostname}:${getEnv("NEXT_PUBLIC_ONPREMISE_BACKEND_PORT")}/ws`;
};

const useWebsocket = (urlPath: string, isEnabled: boolean) => {
  const [wsData, setWSData] = useState<any>(null);
  const [socket, setSocket] = useState<W3CWebSocket | null>(null);
  // useful in showing the states for the socket connections
  const [isWSConnectionOpen, setConnectionOpen] = useState(false);
  const [isWSConnectionLoading, setConnectionLoading] = useState(true);
  const [isWSConnectionClosed, setConnectionClosed] = useState(false);

  useEffect(() => {
    if (!isEnabled) {
      return;
    }
    let websocket: W3CWebSocket;
    let connectionTimeout: NodeJS.Timeout;

    const connect = () => {
      const socketUrl = getWebsocketURL() + urlPath;
      websocket = new W3CWebSocket(socketUrl);

      websocket.onopen = () => {
        setConnectionOpen(true);
        setConnectionLoading(false);
        clearTimeout(connectionTimeout);
        setSocket(websocket);
      };

      websocket.onerror = () => {
        setConnectionOpen(false);
        setConnectionLoading(false);
        websocket.close();
      };

      websocket.onmessage = (event: IMessageEvent) => {
        if (typeof event.data === "string") {
          const data = JSON.parse(event.data);
          setWSData(data);
        }
      };

      websocket.onclose = (e) => {
        if (!isWSConnectionClosed) {
          console.log("Socket is closed. Trying to reconnect...");
          connectionTimeout = setTimeout(() => {
            connect();
          }, 5000);
        }
      };
    };

    connect();

    return () => {
      if (websocket?.readyState === 1) {
        setConnectionClosed(true);
        setConnectionOpen(false);
        websocket?.close();
      }
    };
  }, []);

  return {
    socket,
    wsData,
    isWSConnectionOpen,
    isWSConnectionLoading,
  };
};

export default useWebsocket;
